<template>
  <div class="live-home">
    <div class="main-content">
      <!-- 青少年模式入口条 -->
      <div class="minor-entry-bar" @click="openMinorGuide">
        <svg class="me-shield" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3z" fill="currentColor"/>
        </svg>
        <span class="me-text">
          青少年模式<template v-if="minorOpted"> · 已开启</template><template v-else-if="minorLocked"> · 已强制开启</template><template v-else> · 未开启</template>
        </span>
        <span class="me-arrow" aria-hidden="true">›</span>
      </div>
      <!-- 轮播/主推区域 -->
      <div class="banner-section">
        <div class="container">
          <div class="live-showcase">
            <div class="main-live">
              <video 
                v-if="videoDetail && videoDetail.video_url && !videoError" 
                class="main-live-video"
                :src="fullVideoUrl" 
                autoplay
                loop
                muted
                @error="handleVideoError"
                @timeupdate="handleTimeUpdate"
                ref="videoPlayer"
              ></video>
              <img 
                v-else
                :src="getFullImageUrl(currentLive.cover)" 
                alt="主推内容" 
                class="main-live-img"
              >
              <!-- 底部遮罩信息 -->
              <div class="live-info-overlay">
                <h3 class="live-title-overlay">{{ videoDetail?.title || currentLive.title }}</h3>
                <div class="video-description">{{ videoDetail?.description || '暂无描述' }}</div>
                <div class="live-stats">
                  <span class="live-viewers">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
                    {{ formatViews(videoDetail?.views || currentLive.views) }}
                  </span>
                  <span class="live-tag">{{ minorMode ? '适龄直播' : (videoDetail?.category?.name || currentLive.category?.name || '热门') }}</span>
                </div>
              </div>
              <!-- 进入直播间按钮 -->
              <div class="enter-live-room">
                <button class="enter-live-btn" @click="enterLiveRoom(currentLive.id)">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                  立即观看
                </button>
              </div>
              <!-- 视频控制栏 -->
              <div class="video-controls-bar">
                <div class="video-controls-right">
                  <button class="enter-live-room-btn" @click="enterLiveRoom(currentLive.id)">
                    进入直播间
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                  </button>
                </div>
              </div>
            </div>
            <div class="side-lives">
              <div 
                v-for="(video, index) in filteredSideVideos" 
                :key="video.id"
                class="side-live-item"
                :class="{ active: currentLiveIndex === index }"
                @mouseover="setCurrentLive(index)"
              >
                <div class="bubble-container">
                  <div class="bubble-pointer" v-if="currentLiveIndex === index"></div>
                  <img :src="getFullImageUrl(video.cover)" :alt="video.title">
                  <span v-if="minorMode" class="minor-badge side">
                    <svg viewBox="0 0 24 24" width="10" height="10" aria-hidden="true">
                      <path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3z" fill="currentColor"/>
                    </svg>
                    青少年
                  </span>
                  <div class="live-viewers-bubble">{{ formatViews(video.views) }}</div>
                  <div class="enter-live-overlay">
                    <button class="enter-live-small-btn" @click.stop="enterLiveRoom(video.id)">观看</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 热门推荐区域 -->
      <div class="recommend-section">
        <div class="container">
          <div class="section-header">
            <h2 class="section-title">{{ currentCategory !== null && currentCategory !== undefined ? (currentCategoryName + ' · 精选') : (minorMode ? '适龄推荐' : '热门推荐') }}</h2>
            <div class="section-actions">
              <button v-if="currentCategory !== null && currentCategory !== undefined" class="refresh-btn" @click="clearCategoryFilter">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" class="icon-refresh"><path d="M13 3a9 9 0 1 0 8.366 5.5"/><path d="M13 3v5h5"/></svg>
                返回热门
              </button>
              <button v-else class="refresh-btn" @click="refreshRecommendVideos">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" class="icon-refresh"><path d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg>
                换一批
              </button>
            </div>
          </div>
          <div class="live-grid">
            <div class="live-card" v-for="video in filteredRecommendVideos" :key="video.id" @click="goToVideo(video)">
              <div class="live-thumbnail">
                <img :src="getFullImageUrl(video.cover)" :alt="video.title" loading="lazy">
                <!-- 青少年模式：右上角适龄内容角标 -->
                <span v-if="minorMode" class="minor-badge">
                  <svg viewBox="0 0 24 24" width="11" height="11" aria-hidden="true">
                    <path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3z" fill="currentColor"/>
                  </svg>
                  青少年
                </span>
                <div class="live-status">
                  <span v-if="video.isLive" class="live-badge-live"><i></i>直播中</span>
                  <span class="viewers">{{ formatViews(video.views) }}观看</span>
                </div>
              </div>
              <div class="live-card-info">
                <h4 class="live-card-title">{{ video.title }}</h4>
                <span class="live-card-author" @click.stop="goToAnchor(video.user?.id)">{{ video.user?.nickname || '未知作者' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 分类视频区域 -->
      <div class="category-section" v-for="category in filteredCategoryList" :key="category.id">
        <div class="container">
          <div class="section-header">
            <h2 class="section-title">{{ category.name }}</h2>
          </div>
          <div class="live-grid">
            <div class="live-card" v-for="video in getVideosByCategory(category.id)" :key="video.id" @click="goToVideo(video.id)">
              <div class="live-thumbnail">
                <img :src="getFullImageUrl(video.cover)" :alt="video.title" loading="lazy">
                <div class="live-status">
                  <span class="viewers">{{ formatViews(video.views) }}观看</span>
                </div>
              </div>
              <div class="live-card-info">
                <h4 class="live-card-title">{{ video.title }}</h4>
                <span class="live-card-author">{{ video.user?.nickname || '未知作者' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <PageFooter />

    <!-- 青少年模式引导弹窗 -->
    <transition name="mg-fade">
      <div class="minor-guide-mask" v-if="showMinorGuide" @click.self="closeMinorGuide">
        <div class="minor-guide-card">
          <div class="mg-icon">
            <svg viewBox="0 0 24 24" width="34" height="34" aria-hidden="true">
              <path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3z" fill="currentColor"/>
            </svg>
          </div>
          <h3 class="mg-title">{{ minorGuideTitle }}</h3>
          <p class="mg-desc">
            <template v-if="!isLoggedIn">
              您还未开启青少年模式。若本机由未成年人使用，建议登录后开启：<br>
            </template>
            <template v-else-if="minorLocked">
              实名信息显示为未成年人，青少年模式已强制开启：<br>
            </template>
            <template v-else>
              开启后将守护未成年人健康上网：<br>
            </template>
            · 每日使用时长限制，宵禁时段 {{ cfgNightStart }}:00–{{ cfgNightEnd }}:00 暂停使用<br>
            · 仅展示适龄免费内容，屏蔽充值、打赏与弹幕发送<br>
            · 需家长密码方可延长使用时间
          </p>
          <div class="mg-actions">
            <button class="mg-btn primary" :disabled="minorSubmitting" @click="onMinorGuidePrimary">
              {{ minorPrimaryText }}
            </button>
            <button class="mg-btn ghost" @click="closeMinorGuide">稍后再说</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { getVideoList, getVideoDetail, getCategoryList } from '../api/video';
import { getLivingList } from '../api/liveRoom';
import { getRealnameStatus, setMinorMode, setParentPwd } from '../api/realname';
import { confirmDialog } from './ConfirmDialog.vue';
import { shouldShowGuide } from '../utils/minorGuide';
import { BASE_URL } from '../api/config';
import PageFooter from './Footer.vue';

export default {
  name: 'HelloWorld',
  components: {
    PageFooter
  },
  data() {
    return {
      currentLiveIndex: 0,
      videoList: [],
      recommendList: [],
      categoryList: [],
      videoDetail: null,
      videoError: false,
      loading: false,
      error: null,
      isPlaying: true,
      currentTime: '00:00',
      duration: '00:00',
      randomSeed: 0,
      currentCategory: null,
      minorMode: false,
      // minorOpted：用户是否主动开启过（游客恒 false），驱动"未开启"提醒
      minorOpted: false,
      // minorLocked：强制生效、用户不可自行关闭
      minorLocked: false,
      // 是否已设置家长密码：开启前无密码需先设置
      hasParentPwd: false,
      minorSubmitting: false,
      // 青少年模式下首页保留的内容条数（后端 minor_room_limit 下发，默认 4）
      minorRoomLimit: 4,
      isLoggedIn: !!localStorage.getItem('token'),
      cfgNightStart: 22,
      cfgNightEnd: 8,
      showMinorGuide: false
    }
  },
  watch: {
    '$route.query.category'() {
      this.applyCategoryFilter();
    }
  },
  computed: {
    // 弹窗标题：绝不能对"已强制生效"的用户说"尚未开启"，也不能对游客说"已开启"。
    // 判断顺序 minorOpted → minorLocked（已实名未成年强制生效）→ 其余 = 未开启
    minorGuideTitle() {
      if (this.minorOpted) return '青少年模式已开启';
      if (this.minorLocked) return '青少年模式已强制开启';
      return '您尚未开启青少年模式';
    },
    // 主按钮文案随登录态与开启态变化。
    // minorLocked（已实名未成年强制生效）时不能显示"立即开启"——已经开着了，
    // 引导去实名页查看；此状态不可关闭，实名为成年人才有开关选择权。
    minorPrimaryText() {
      if (this.minorSubmitting) return '处理中…';
      if (!this.isLoggedIn) return '登录 / 注册';
      if (this.minorOpted) return '设置家长密码';
      if (this.minorLocked) return '查看实名认证';
      return '立即开启';
    },
    currentLive() {
      return this.videoList.length > 0 ? this.videoList[this.currentLiveIndex] : {
        title: '加载中...',
        views: 0,
        cover: '',
        category: { name: '热门' }
      };
    },
    fullVideoUrl() {
      if (!this.videoDetail?.video_url) return '';
      if (this.videoDetail.video_url.startsWith('http')) {
        return this.videoDetail.video_url;
      }
      return `${BASE_URL}${this.videoDetail.video_url}`;
    },
    randomRecommendVideos() {
      if (!this.recommendList || this.recommendList.length === 0) {
        return [];
      }
      // 真实直播固定排在前部，视频参与随机洗牌
      const lives = this.recommendList.filter(v => v.isLive);
      const videos = this.recommendList.filter(v => !v.isLive);
      if (videos.length <= 8 - lives.length) {
        return this.recommendList;
      }
      // 使用随机种子生成稳定的随机序列
      const seed = this.randomSeed || Date.now();
      const shuffled = [...videos];
      let s = seed;
      for (let i = shuffled.length - 1; i > 0; i--) {
        s = (s * 1103515245 + 12345) & 0x7fffffff;
        const j = s % (i + 1);
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return [...lives, ...shuffled].slice(0, 8);
    },
    filteredRecommendVideos() {
      // currentCategory 为 null 表示未筛选；为 0 表示"适龄直播"分类
      const base = (this.currentCategory === null || this.currentCategory === undefined)
        ? this.randomRecommendVideos
        : this.recommendList.filter(v => Number(v.category_id) === Number(this.currentCategory));
      if (!this.minorMode) return base;
      // 青少年模式按白名单收敛：必须明确标记免费才展示。
      // 原先放过 is_free === undefined，等于"字段缺失即通行"，
      // 一旦接口少返字段付费内容就会漏出，白名单不能这么写。
      return base
        .filter(v => Number(v.is_free) === 1 || Number(v.minor_safe) === 1)
        .slice(0, this.minorRoomLimit);
    },
    // 侧边推荐位：青少年模式同样走白名单 + 条数上限
    filteredSideVideos() {
      if (!this.minorMode) return this.videoList;
      return this.videoList
        .filter(v => Number(v.is_free) === 1 || Number(v.minor_safe) === 1)
        .slice(0, this.minorRoomLimit);
    },
    filteredCategoryList() {
      // 青少年模式：隐藏细分分类，仅保留"适龄推荐"
      if (this.minorMode) return [];
      if (this.currentCategory === null || this.currentCategory === undefined) return this.categoryList;
      const catId = Number(this.currentCategory);
      return this.categoryList.filter(c => Number(c.id) === catId);
    },
    currentCategoryName() {
      if (this.currentCategory === null || this.currentCategory === undefined) return '';
      const catId = Number(this.currentCategory);
      const cat = this.categoryList.find(c => Number(c.id) === catId);
      return cat ? cat.name : '';
    }
  },
  methods: {
    setCurrentLive(index) {
      this.currentLiveIndex = index;
      this.fetchVideoDetail(this.videoList[index].id);
    },
    handleVideoError(e) {
      this.videoError = true;
      console.warn('视频加载失败，已切换为封面图', e);
    },
    formatViews(views) {
      if (views >= 10000) {
        return (views / 10000).toFixed(1) + '万';
      }
      return views || 0;
    },
    async fetchVideos() {
      this.loading = true;
      this.error = null;
      try {
        // 并行拉取视频列表与真实直播房间，统一展示在首页
        const [videoRes, liveRes] = await Promise.all([
          getVideoList().catch(() => null),
          getLivingList({ page: 1, pagesize: 60 }).catch(() => null)
        ]);
        const videos = (videoRes && videoRes.data && videoRes.data.list && videoRes.data.list.length > 0)
          ? videoRes.data.list
          : [];
        // 后端下发的青少年模式保留条数（后台 minor_room_limit 可配）
        const rl = videoRes && videoRes.data && Number(videoRes.data.minor_room_limit);
        if (rl > 0) this.minorRoomLimit = rl;
        const lives = this.normalizeLives(liveRes);
        this.videoList = videos.slice(0, 5);
        this.recommendList = [...lives, ...videos];
        if (this.videoList.length > 0) {
          await this.fetchVideoDetail(this.videoList[0].id);
        } else if (this.recommendList.length === 0) {
          this.error = '没有获取到视频数据';
        }
      } catch (err) {
        console.error('获取视频列表失败:', err);
        this.error = '获取视频列表失败: ' + err.message;
      } finally {
        this.loading = false;
      }
    },
    // 将正在直播的房间映射为与视频卡片兼容的结构
    normalizeLives(res) {
      if (!res || res.code !== 1) return [];
      const list = res.data?.data || res.data?.list || [];
      const arr = Array.isArray(list) ? list : [];
      return arr.map(r => ({
        id: r.id,
        title: r.title || '未命名直播间',
        cover: r.cover || r.cover_image || r.image || '',
        views: r.view_count || r.views || 0,
        user: { id: r.user_id, nickname: r.nickname || r.anchor_name || '主播' },
        isLive: true,
        category_id: r.category_id || 0,
        category_name: r.category_name || '',
        // 透传后端的适龄标记，供卡片右上角角标使用
        minor_safe: r.minor_safe || 0,
        is_free: 1
      }));
    },
    async fetchVideoDetail(id) {
      try {
        this.videoError = false;
        const response = await getVideoDetail(id);
        if (response && response.data) {
          this.videoDetail = response.data;
          this.isPlaying = true;
          if (this.$refs.videoPlayer) {
            setTimeout(() => {
              if (this.$refs.videoPlayer) {
                this.$refs.videoPlayer.load();
              }
            }, 100);
          }
        } else {
          this.videoError = true;
        }
      } catch (err) {
        console.error('获取视频详情失败:', err);
        this.videoError = true;
      }
    },
    enterLiveRoom(id) {
      this.$router.push(`/live/${id}`);
    },
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    },
    handleTimeUpdate(event) {
      const video = event.target;
      this.currentTime = this.formatTime(video.currentTime);
      this.duration = this.formatTime(video.duration || 0);
    },
    async fetchCategories() {
      try {
        const response = await getCategoryList();
        if (response && response.data && response.data.list) {
          this.categoryList = response.data.list;
        }
      } catch (err) {
        console.error('获取分类列表失败:', err);
      }
    },
    getVideosByCategory(categoryId) {
      const cid = Number(categoryId);
      return this.recommendList.filter(video => Number(video.category_id) === cid);
    },
    getFullImageUrl(url) {
      if (!url) return '';
      if (url.startsWith('http')) return url;
      return `${BASE_URL}${url}`;
    },
    goToVideo(item) {
      const id = typeof item === 'object' ? item.id : item;
      // 真实直播进入直播间页，视频进入直播播放页
      this.$router.push((typeof item === 'object' && item.isLive) ? `/room/${id}` : `/live/${id}`);
    },
    goToAnchor(anchorId) {
      if (!anchorId) return;
      this.$router.push({ name: 'AnchorHome', params: { id: anchorId } });
    },
    refreshRecommendVideos() {
      this.randomSeed = Date.now();
    },
    applyCategoryFilter() {
      // 未传 category 参数时为 null（未筛选）；传 0 表示"适龄直播"分类
      const raw = this.$route.query.category;
      this.currentCategory = (raw === undefined || raw === '' || raw === null) ? null : Number(raw);
    },
    clearCategoryFilter() {
      this.$router.replace({ path: '/', query: {} });
      this.currentCategory = null;
    },
    /**
     * 拉取青少年模式状态并决定是否弹提醒。
     * 触发时机：首页 mounted、登录态变化事件、从登录页返回。
     * 数据来源：GET /api/realname/status（游客亦可调用）。
     */
    async fetchMinorMode(opts) {
      const options = opts || {};
      // 同步登录态：登录后经 replace('/') 回首页时组件可能复用，需重新读取
      this.isLoggedIn = !!localStorage.getItem('token');
      let status = null;
      try {
        const res = await getRealnameStatus();
        status = (res && res.data) || null;
      } catch (e) {
        status = null;
      }
      if (status) {
        this.minorMode = Boolean(status.minor_mode);
        this.minorOpted = Number(status.minor_opted) === 1;
        this.minorLocked = Number(status.minor_locked) === 1;
        this.hasParentPwd = !!status.has_parent_pwd;
        if (status.config) {
          this.cfgNightStart = Number(status.config.night_start) || 22;
          this.cfgNightEnd = Number(status.config.night_end) || 8;
        }
      } else {
        // 接口异常：不猜测状态，也不弹窗，避免误报打扰
        this.minorMode = false;
        this.minorOpted = false;
        this.minorLocked = false;
        return;
      }

      // 只要没开启就弹：不做会话去重，也不读"不再提示"标记。
      // 每次进入首页（mounted / activated）都会走到这里，实现"未开启每次都提醒"。
      if (shouldShowGuide(status, options.force)) {
        this.showMinorGuide = true;
      }
    },
    // 用户主动点击入口条：无条件展开
    openMinorGuide() {
      this.showMinorGuide = true;
    },
    // 关闭弹窗：仅关闭本次，下次进入首页仍会提醒
    closeMinorGuide() {
      this.showMinorGuide = false;
    },
    onMinorGuidePrimary() {
      // 未登录：先记下待复弹标记，再带 redirect 跳登录
      // 与 minorPrimaryText 共用同一判据 isLoggedIn，避免按钮文案与行为不一致
      if (!this.isLoggedIn) {
        this.showMinorGuide = false;
        this.$router.push({ path: '/login', query: { redirect: '/' } });
        return;
      }
      // 已开启：引导去设置家长密码；
      // 已开启或被强制（已实名未成年）：跳实名页查看/管理（此时调 setMinorMode(1) 无意义）
      if (this.minorOpted || this.minorLocked) {
        this.showMinorGuide = false;
        this.$router.push({ path: '/user', query: { tab: 'realname' } });
        return;
      }
      this.enableMinorMode();
    },
    async enableMinorMode() {
      if (!localStorage.getItem('token')) {
        this.showMinorGuide = false;
        this.$router.push({ path: '/login', query: { redirect: '/' } });
        return;
      }
      // 尚未设置家长密码：先设置（与个人中心口径一致），成功后自动开启。
      // 不先设密码的话，之后关闭时后端一律要求验密码，会出现开了关不掉的死胡同。
      if (!this.hasParentPwd) {
        const pwd = await confirmDialog({
          title: '设置家长密码',
          message: '开启青少年模式前，请先设置家长密码（6-30 位），用于日后关闭或调整限制：',
          showInput: true,
          placeholder: '家长密码（6-30 位）',
          okText: '确认设置'
        });
        if (!pwd) return; // 取消：不开启，弹窗与状态保持原状
        this.minorSubmitting = true;
        try {
          const res = await setParentPwd(pwd);
          if (!res || res.code !== 1) {
            window.alert((res && res.msg) || '密码设置失败');
            return;
          }
          this.hasParentPwd = true;
        } catch (e) {
          window.alert((e && e.message) || '密码设置失败，请重试');
          return;
        } finally {
          this.minorSubmitting = false;
        }
      }
      this.minorSubmitting = true;
      try {
        const res = await setMinorMode(1);
        if (res && res.code === 1) {
          this.minorMode = true;
          this.minorOpted = true;
          this.showMinorGuide = false;
          window.alert('已开启青少年模式');
          window.dispatchEvent(new Event('minor-mode-changed'));
        } else {
          window.alert((res && res.msg) || '开启失败');
        }
      } catch (e) {
        // 拦截器把业务错误也 reject 成 Error(msg)，优先展示真实原因
        window.alert((e && e.message) || '开启失败，请重试');
      } finally {
        this.minorSubmitting = false;
      }
    },
    onMinorModeChanged() {
      this.fetchMinorMode();
    }
  },
  mounted() {
    this.applyCategoryFilter();
    this.fetchVideos();
    this.fetchCategories();
    this.fetchMinorMode();
    // 开关变更（如在个人中心开启/关闭）后同步首页状态
    window.addEventListener('minor-mode-changed', this.onMinorModeChanged);
  },
  beforeUnmount() {
    window.removeEventListener('minor-mode-changed', this.onMinorModeChanged);
  },
  // hash 路由下从登录页 replace('/') 回首页时组件可能被复用，
  // activated 覆盖 keep-alive 场景，确保回到首页能复弹
  activated() {
    this.fetchMinorMode();
  }
}
</script>

<style scoped>
.live-home {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
}

.main-content {
  flex: 1;
}

.container {
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 20px;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
}

/* ==================== 轮播/主推区域 ==================== */
.banner-section {
  padding: 24px 0 0;
  position: relative;
  background: linear-gradient(180deg, #0f0f1a 0%, #1a1a2e 40%, #f0f2f5 100%);
}

.live-showcase {
  display: flex;
  gap: 12px;
  border-radius: 16px;
  overflow: hidden;
  height: 500px;
  min-width: 0;
}

.main-live {
  flex: 1;
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  background: #000;
  transition: box-shadow 0.3s;
  min-width: 0;
}

.main-live:hover {
  box-shadow: 0 8px 32px rgba(0,0,0,0.3);
}

.main-live-img,
.main-live-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.live-info-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 30px;
  box-sizing: border-box;
  background: linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 60%);
  color: #fff;
  z-index: 2;
}

.live-title-overlay {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
  text-shadow: 0 2px 8px rgba(0,0,0,0.3);
}

.video-description {
  color: rgba(255,255,255,0.75);
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 12px;
  max-height: 44px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.live-stats {
  display: flex;
  align-items: center;
  gap: 12px;
}

.live-viewers {
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
  color: rgba(255,255,255,0.9);
}

.live-tag {
  background: rgba(255,150,0,0.9);
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

/* 进入直播间按钮叠加 */
.enter-live-room {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.35s ease;
  z-index: 3;
  background: rgba(0,0,0,0.15);
}

.main-live:hover .enter-live-room {
  opacity: 1;
}

.enter-live-btn {
  padding: 14px 36px;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  border: none;
  border-radius: 30px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 24px rgba(255,106,0,0.4);
  display: flex;
  align-items: center;
  gap: 8px;
}

.enter-live-btn:hover {
  transform: translateY(-2px) scale(1.03);
  box-shadow: 0 12px 32px rgba(255,106,0,0.5);
}

/* 侧边视频列表 */
.side-lives {
  width: 200px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 0;
}

.side-live-item {
  position: relative;
  cursor: pointer;
  height: 90px;
  overflow: visible;
  transition: all 0.25s ease;
  border-radius: 12px;
}

.bubble-container {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.side-live-item.active .bubble-container {
  border-color: #ff9600;
  box-shadow: 0 0 20px rgba(255,150,0,0.3);
}

.bubble-pointer {
  position: absolute;
  left: -10px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-right: 10px solid #ff9600;
  z-index: 2;
  filter: drop-shadow(0 0 4px rgba(255,150,0,0.5));
}

.side-live-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.35s ease;
}

.side-live-item:hover img {
  transform: scale(1.08);
}

.live-viewers-bubble {
  position: absolute;
  bottom: 6px;
  right: 6px;
  background: rgba(0,0,0,0.75);
  color: #fff;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  backdrop-filter: blur(4px);
}

.enter-live-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 2;
  background: rgba(0,0,0,0.4);
}

.side-live-item:hover .enter-live-overlay {
  opacity: 1;
}

.enter-live-small-btn {
  padding: 5px 14px;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}

.enter-live-small-btn:hover {
  transform: scale(1.08);
}

/* 视频控制栏 */
.video-controls-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 44px;
  background: linear-gradient(to top, rgba(0,0,0,0.85), rgba(0,0,0,0));
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  box-sizing: border-box;
  z-index: 4;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.main-live:hover .video-controls-bar {
  opacity: 1;
}

.video-controls-right {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.video-time {
  color: rgba(255,255,255,0.8);
  font-size: 12px;
  font-family: monospace;
}

.enter-live-room-btn {
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.enter-live-room-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255,106,0,0.4);
}

/* ==================== 区域标题通用 ==================== */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.section-title {
  font-size: 24px;
  color: #1a1a2e;
  position: relative;
  padding-left: 16px;
  font-weight: 700;
  margin: 0;
}

.section-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 24px;
  background: linear-gradient(180deg, #ff6a00, #ff9600);
  border-radius: 2px;
}

.section-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.refresh-btn {
  background: #fff;
  color: #666;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 8px 18px;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.25s ease;
}

.refresh-btn:hover {
  border-color: #ff9600;
  color: #ff9600;
  box-shadow: 0 2px 8px rgba(255,150,0,0.15);
}

.icon-refresh {
  transition: transform 0.5s ease;
}

.refresh-btn:hover .icon-refresh {
  transform: rotate(180deg);
}

/* ==================== 热门推荐 ==================== */
.recommend-section,
.category-section {
  padding: 32px 0;
}

.recommend-section {
  padding-top: 40px;
}

.live-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.live-card {
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border: 1px solid rgba(0,0,0,0.02);
}

.live-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 28px rgba(0,0,0,0.12);
}

.live-thumbnail {
  position: relative;
  overflow: hidden;
  aspect-ratio: 16/9;
  background: #f0f0f0;
}

.live-thumbnail img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.live-card:hover .live-thumbnail img {
  transform: scale(1.08);
}

.live-status {
  position: absolute;
  bottom: 10px;
  left: 10px;
  background: rgba(0,0,0,0.7);
  backdrop-filter: blur(4px);
  color: #fff;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}
.live-badge-live {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #ff4d4f;
  font-weight: 700;
}
.live-badge-live i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ff4d4f;
  animation: liveBlink 1.2s infinite;
}
@keyframes liveBlink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.live-card-info {
  padding: 14px 16px 16px;
}

.live-card-title {
  font-size: 15px;
  color: #1a1a2e;
  font-weight: 600;
  line-height: 1.4;
  margin: 0 0 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.live-card-author {
  font-size: 13px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  transition: color 0.2s;
}
.live-card-author:hover {
  color: #ff6a00;
}

.live-card-author::before {
  content: '';
  display: inline-block;
  width: 16px;
  height: 16px;
  background: #e2e8f0;
  border-radius: 50%;
  flex-shrink: 0;
}

/* ==================== 响应式 ==================== */
@media (max-width: 1200px) {
  .live-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 992px) {
  .live-showcase {
    height: auto;
    flex-direction: column;
  }
  .main-live {
    width: 100%;
    min-height: 400px;
  }
  .side-lives {
    width: 100%;
    flex-direction: row;
    gap: 8px;
    overflow-x: auto;
    padding: 8px 0;
  }
  .side-live-item {
    flex-shrink: 0;
    width: 160px;
    height: 90px;
  }
  .bubble-pointer {
    display: none;
  }
  .live-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .banner-section {
    padding-top: 16px;
  }

  .live-showcase {
    gap: 8px;
  }

  .main-live {
    min-height: 0;
    aspect-ratio: 16 / 9;
  }

  .live-info-overlay {
    padding: 16px;
  }

  .live-title-overlay {
    font-size: 18px;
    margin-bottom: 4px;
  }

  .video-description {
    display: none;
  }

  .enter-live-room {
    opacity: 1;
    background: rgba(0, 0, 0, 0.16);
  }

  .enter-live-btn {
    padding: 10px 20px;
    font-size: 15px;
  }

  .video-controls-bar {
    opacity: 1;
    padding: 0 10px;
  }

  .live-grid {
    grid-template-columns: 1fr;
  }
  .section-title {
    font-size: 20px;
  }
  .container {
    padding: 0 12px;
  }
}

/* ==================== 青少年模式入口条 ==================== */
.minor-entry-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 1300px;
  margin: 16px auto 12px;
  padding: 10px 16px;
  box-sizing: border-box;
  background: linear-gradient(90deg, #e9f7f0, #eef3fb);
  border: 1px solid #cfe9dd;
  border-radius: 12px;
  cursor: pointer;
  color: #1f7a5a;
  font-size: 14px;
  font-weight: 600;
  transition: box-shadow .2s ease;
}
.minor-entry-bar:hover {
  box-shadow: 0 6px 18px rgba(31, 122, 90, 0.14);
}
.me-shield {
  flex: 0 0 auto;
}
.me-text {
  flex: 1 1 auto;
}
.me-arrow {
  color: #1f7a5a;
  font-size: 20px;
  line-height: 1;
}

/* ==================== 适龄内容角标（卡片右上角） ==================== */
.minor-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(31, 122, 90, 0.92);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: .2px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
  pointer-events: none;
  white-space: nowrap;
}
.minor-badge.side {
  top: 6px;
  right: 6px;
  padding: 2px 6px;
  font-size: 10px;
}

/* ==================== 青少年模式引导弹窗 ==================== */
.minor-guide-mask {
  position: fixed;
  inset: 0;
  z-index: 3200;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
}
.minor-guide-card {
  width: 360px;
  max-width: 88vw;
  background: #fff;
  border-radius: 18px;
  padding: 30px 26px 26px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
}
.mg-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 14px;
  border-radius: 50%;
  background: #e9f7f0;
  color: #1f7a5a;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mg-title {
  margin: 0 0 12px;
  font-size: 19px;
  color: #2b2b2b;
}
.mg-desc {
  margin: 0 0 22px;
  font-size: 13px;
  line-height: 1.9;
  color: #6b6b6b;
  text-align: left;
}
.mg-actions {
  display: flex;
  gap: 12px;
}
.mg-btn {
  flex: 1 1 0;
  border: none;
  border-radius: 999px;
  padding: 11px 0;
  font-size: 15px;
  cursor: pointer;
}
.mg-btn.primary {
  background: #1f7a5a;
  color: #fff;
}
.mg-btn.primary:hover {
  background: #19674c;
}
.mg-btn.ghost {
  background: #f2f2f2;
  color: #666;
}
.mg-btn.ghost:hover {
  background: #e8e8e8;
}
.mg-btn:disabled {
  opacity: .6;
  cursor: not-allowed;
}
.mg-fade-enter-active,
.mg-fade-leave-active {
  transition: opacity .22s ease;
}
.mg-fade-enter-from,
.mg-fade-leave-to {
  opacity: 0;
}
</style>
